import { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, Calendar, Star, Users, Settings, MapPin, Shield, ShieldCheck, ShieldAlert, ShieldQuestion, LayoutGrid, List, Car, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import BookingsCalendar from '../components/BookingsCalendar';
import CalendarSubscribe from '../components/CalendarSubscribe';
import AddToCalendarButton from '../components/AddToCalendarButton';
import CleanerBookingResponseActions from '../components/CleanerBookingResponseActions';
import ReferralPanel from '../components/ReferralPanel';
import Header from '../components/Header';
import ProfilePhotoUploader from '../components/ProfilePhotoUploader';
import StripeConnectCard from '../components/StripeConnectCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CleanerDashboard = ({ user, setUser }) => {
  const { t } = useLanguage();
  const [bookingsView, setBookingsView] = useState('list');
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [profileData, setProfileData] = useState({
    bio: '',
    years_experience: 1,
    certifications: [],
    services: [],
    hourly_rate: 30,
    zip_code: '',
    latitude: null,
    longitude: null,
    supplies_default: 'cleaner',
    service_zip_codes: [],
    service_radius_miles: 10,
    availability: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });
  const [newCert, setNewCert] = useState('');
  const [newService, setNewService] = useState('');
  const [locationStatus, setLocationStatus] = useState('');
  const [bgCheckLoading, setBgCheckLoading] = useState(false);

  const startBackgroundCheck = async () => {
    setBgCheckLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/background-check/invite`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.invitation_url;
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start background check');
      setBgCheckLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');

      // Public platform settings — non-fatal if it fails
      try {
        const settingsRes = await axios.get(`${API}/platform/settings`);
        setPlatformSettings(settingsRes.data);
      } catch (_) { /* ignore */ }

      try {
        const profileRes = await axios.get(`${API}/cleaners/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(profileRes.data);
        setProfileData({
          bio: profileRes.data.bio,
          years_experience: profileRes.data.years_experience,
          certifications: profileRes.data.certifications,
          services: profileRes.data.services,
          hourly_rate: profileRes.data.hourly_rate,
          zip_code: profileRes.data.zip_code,
          latitude: profileRes.data.latitude,
          longitude: profileRes.data.longitude,
          supplies_default: profileRes.data.supplies_default || 'cleaner',
          service_zip_codes: profileRes.data.service_zip_codes || [],
          service_radius_miles: profileRes.data.service_radius_miles || 10,
          availability: profileRes.data.availability
        });
      } catch (err) {
        // Profile doesn't exist yet
        setShowProfileForm(true);
      }

      const bookingsRes = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
        });
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const method = profile ? 'put' : 'post';
      
      await axios[method](`${API}/cleaners/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Profile saved successfully!');
      setShowProfileForm(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  const addCertification = () => {
    if (newCert.trim()) {
      setProfileData({
        ...profileData,
        certifications: [...profileData.certifications, newCert.trim()]
      });
      setNewCert('');
    }
  };

  const addService = () => {
    if (newService.trim()) {
      setProfileData({
        ...profileData,
        services: [...profileData.services, newService.trim()]
      });
      setNewService('');
    }
  };

  const toggleAvailability = (day, time) => {
    const daySlots = profileData.availability[day] || [];
    const newSlots = daySlots.includes(time)
      ? daySlots.filter(t => t !== time)
      : [...daySlots, time];
    
    setProfileData({
      ...profileData,
      availability: {
        ...profileData.availability,
        [day]: newSlots
      }
    });
  };

  const updateLocation = async () => {
    setLocationStatus('Detecting location...');
    if (!navigator.geolocation) {
      setLocationStatus('Geolocation not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        try {
          const token = localStorage.getItem('token');
          await axios.put(
            `${API}/cleaners/location`,
            { latitude: lat, longitude: lng },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setLocationStatus(`Location updated: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          setProfileData({ ...profileData, latitude: lat, longitude: lng });
          fetchData();
        } catch (error) {
          setLocationStatus('Failed to update location');
        }
      },
      (error) => {
        setLocationStatus(`Error: ${error.message}`);
      }
    );
  };

  const timeSlots = ['08:00', '08:15', '08:30', '08:45', '09:00', '09:15', '09:30', '09:45', '10:00', '10:15', '10:30', '10:45', '11:00', '11:15', '11:30', '11:45', '12:00', '12:15', '12:30', '12:45', '13:00', '13:15', '13:30', '13:45', '14:00', '14:15', '14:30', '14:45', '15:00', '15:15', '15:30', '15:45', '16:00', '16:15', '16:30', '16:45', '17:00', '17:15', '17:30', '17:45'];
  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const totalEarnings = bookings
    .filter(b => b.status === 'completed' || b.status === 'confirmed')
    .reduce((sum, b) => sum + (b.cleaner_earnings || b.total_amount * 0.85), 0);

  const cancelCompensation = bookings
    .filter(b => b.status === 'cancelled_late' && b.cleaner_cancel_earnings)
    .reduce((sum, b) => sum + b.cleaner_cancel_earnings, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} setUser={setUser} />
        <div className="flex items-center justify-center py-24">
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={setUser} />
      
      <div className="px-6 md:px-12 lg:px-16 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-text-primary mb-2" data-testid="cleaner-dashboard">
                {t('cleaner.dashboardTitle', 'Cleaner Dashboard')}
              </h1>
              <p className="text-text-secondary">{t('cleaner.dashboardSubtitle', 'Manage your profile and bookings')}</p>
            </div>
            {profile && (
              <button
                onClick={() => setShowProfileForm(!showProfileForm)}
                className="btn-secondary flex items-center gap-2"
                data-testid="edit-profile-button"
              >
                <Settings className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>

          {/* Approval status banner */}
          {profile && profile.approval_status === 'pending_review' && (
            <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="approval-pending-banner">
              <p className="font-semibold">Your profile is under review</p>
              <p className="text-sm mt-1 opacity-90">
                Thanks for signing up! Our team reviews new cleaner profiles within 24 hours. You&apos;ll get an email and notification once you&apos;re approved — then you&apos;ll be visible to customers.
              </p>
            </div>
          )}
          {/* Pre-launch banner — bookings not yet open */}
          {profile && profile.approval_status === 'approved' && platformSettings?.bookings_enabled === false && (
            <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-primary to-primary-dark text-white shadow-lg" data-testid="prelaunch-banner">
              <p className="font-heading text-lg font-semibold flex items-center gap-2">🎉 You&apos;re approved!</p>
              <p className="text-sm mt-1 opacity-95">
                We&apos;re recruiting cleaners in your area first. Customer bookings open soon — you&apos;ll get an email + notification the moment they do. Connect your bank below so you&apos;re ready to earn from day one.
              </p>
            </div>
          )}
          {profile && profile.approval_status === 'rejected' && (
            <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-900" data-testid="approval-rejected-banner">
              <p className="font-semibold">Your application wasn&apos;t approved</p>
              <p className="text-sm mt-1 opacity-90">
                {profile.approval_rejection_reason || 'Please contact office@matchmycleaner.com if you believe this is a mistake.'}
              </p>
            </div>
          )}

          {/* Stats */}
          {profile && !showProfileForm && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <DollarSign className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Net Earnings</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">${(totalEarnings + cancelCompensation).toFixed(2)}</p>
                  <p className="text-xs text-text-secondary mt-1">After platform fee {cancelCompensation > 0 && `• incl. $${cancelCompensation.toFixed(2)} cancel comp.`}</p>
                </div>

                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Total Bookings</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">{bookings.length}</p>
                </div>

                <div className="bg-surface rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-primary-light rounded-full">
                      <Star className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm text-text-secondary">Rating</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-primary">
                    {profile.rating_avg > 0 ? profile.rating_avg.toFixed(1) : 'New'}
                  </p>
                </div>
              </div>

              {/* Platform Agreement Status */}
              <div className="bg-surface rounded-2xl p-6 border border-border mb-8" data-testid="agreement-status">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${user?.agreed_to_terms ? 'bg-primary-light' : 'bg-red-50'}`}>
                    <Shield className={`w-6 h-6 ${user?.agreed_to_terms ? 'text-primary' : 'text-error'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-semibold text-text-primary">Platform Agreement</h3>
                    <p className="text-sm text-text-secondary">
                      {user?.agreed_to_terms
                        ? `You agreed to the Cleaner Platform Agreement${user.terms_agreed_at ? ` on ${new Date(user.terms_agreed_at).toLocaleDateString()}` : ''}. All bookings must go through Match My Cleaner — direct off-platform transactions are not permitted.`
                        : "You haven't signed the Cleaner Platform Agreement yet. Please contact support to complete your onboarding."}
                    </p>
                  </div>
                  <div className={`px-3 py-1 text-white text-xs font-semibold rounded-full ${user?.agreed_to_terms ? 'bg-success' : 'bg-error'}`} data-testid="agreement-badge">
                    {user?.agreed_to_terms ? 'Signed' : 'Not Signed'}
                  </div>
                </div>
              </div>

              {/* Background Check — admin-managed */}
              {(() => {
                const status = profile.bg_check_status || 'not_started';
                const config = {
                  clear: { Icon: ShieldCheck, ring: 'bg-emerald-50', icon: 'text-emerald-600', badgeBg: 'bg-emerald-500', label: 'Verified', title: 'Background-checked ✓', desc: 'Your check is clear. You are eligible to receive bookings.', cta: null },
                  pending_admin_review: { Icon: ShieldQuestion, ring: 'bg-amber-50', icon: 'text-amber-600', badgeBg: 'bg-amber-500', label: 'In Review', title: 'Background Check Pending', desc: 'Our team manually reviews each new cleaner. You\'ll be notified once it\'s cleared. No action needed from you.', cta: null },
                  consider: { Icon: ShieldAlert, ring: 'bg-rose-50', icon: 'text-rose-600', badgeBg: 'bg-rose-500', label: 'Flagged', title: 'Action Required', desc: 'Your report needs admin review. Bookings are paused. Check your email or contact support.', cta: null },
                  suspended: { Icon: ShieldAlert, ring: 'bg-rose-50', icon: 'text-rose-600', badgeBg: 'bg-rose-500', label: 'Suspended', title: 'Verification Incomplete', desc: 'Your check could not be completed. Please contact support.', cta: null },
                  not_started: { Icon: Shield, ring: 'bg-primary-light', icon: 'text-primary', badgeBg: 'bg-text-primary', label: 'Pending', title: 'Background Check', desc: 'You must be background-checked before receiving bookings. Our team handles this — you\'ll be notified once cleared.', cta: null },
                }[status] || { Icon: Shield, ring: 'bg-surface-muted', icon: 'text-text-secondary', badgeBg: 'bg-text-secondary', label: status, title: 'Background Check', desc: '', cta: null };
                const Icon = config.Icon;
                return (
                  <div className="bg-surface rounded-2xl p-6 border border-border mb-8" data-testid="bg-check-card">
                    <div className="flex items-start gap-4 flex-wrap">
                      <div className={`p-3 rounded-full ${config.ring} flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${config.icon}`} />
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">{config.title}</h3>
                        <p className="text-sm text-text-secondary">{config.desc}</p>
                        {profile.bg_check_completed_at && status === 'clear' && (
                          <p className="text-xs text-text-secondary mt-2">
                            Cleared on {new Date(profile.bg_check_completed_at).toLocaleDateString()}
                          </p>
                        )}
                        {platformSettings && status !== 'clear' && (() => {
                          const payer = platformSettings.bg_check_payer || 'platform_recoup';
                          const fee = platformSettings.bg_check_fee ?? 29.99;
                          const labels = {
                            platform: { title: 'Cost: covered by Match My Cleaner', sub: 'No charge to you — we cover the background check fee.' },
                            platform_recoup: { title: `Cost: $${fee.toFixed(2)} (recouped from first payout)`, sub: 'We pay up front — the fee is automatically deducted from your first cleaning payout.' },
                            cleaner: { title: `Cost: $${fee.toFixed(2)} (paid at signup)`, sub: 'Charged before your background check is initiated.' },
                          }[payer];
                          const showWaiver = payer === 'platform_recoup' && platformSettings.bg_check_recoup_waive_enabled;
                          const waiveBookings = platformSettings.bg_check_recoup_waive_bookings ?? 5;
                          const waiveDays = platformSettings.bg_check_recoup_waive_days ?? 30;
                          return (
                            <div className="mt-3 flex flex-col gap-2" data-testid="bg-check-cost-badge">
                              <div className="inline-flex flex-col gap-0.5 bg-primary-light/30 border border-primary/20 rounded-lg px-3 py-2">
                                <span className="text-xs font-semibold text-text-primary">{labels.title}</span>
                                <span className="text-xs text-text-secondary">{labels.sub}</span>
                              </div>
                              {showWaiver && (
                                <div className="inline-flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="bg-check-waiver-promise">
                                  <span className="text-base leading-none mt-0.5">🎁</span>
                                  <div>
                                    <span className="text-xs font-semibold text-emerald-900">
                                      Skip the ${fee.toFixed(2)} deduction
                                    </span>
                                    <p className="text-xs text-emerald-800">
                                      Complete <strong>{waiveBookings} bookings within {waiveDays} days</strong> of signup and we&apos;ll waive the recoupment — your background check is on us.
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 text-white text-xs font-semibold rounded-full ${config.badgeBg}`} data-testid="bg-check-badge">
                          {config.label}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Location Tracker */}
              <div className="bg-surface rounded-2xl p-6 border border-border mb-8" data-testid="location-tracker">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <h3 className="font-heading text-lg font-semibold text-text-primary">Live Location Tracking</h3>
                    </div>
                    <p className="text-sm text-text-secondary">
                      {profile.latitude && profile.longitude
                        ? `Current location: ${profile.latitude.toFixed(4)}, ${profile.longitude.toFixed(4)}`
                        : 'Share your location to be discoverable by nearby customers'}
                    </p>
                    {locationStatus && (
                      <p className="text-xs text-primary mt-2" data-testid="location-status">{locationStatus}</p>
                    )}
                  </div>
                  <button
                    onClick={updateLocation}
                    className="btn-primary flex items-center gap-2"
                    data-testid="update-location-button"
                  >
                    <MapPin className="w-4 h-4" />
                    Update Location
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Profile Form */}
          {showProfileForm && (
            <div className="bg-surface rounded-2xl p-8 border border-border mb-8" data-testid="profile-form">
              <h2 className="font-heading text-2xl font-semibold text-text-primary mb-6">
                {profile ? 'Edit Profile' : 'Create Your Profile'}
              </h2>

              <div className="space-y-6">
                {/* Profile photo (required) */}
                {profile && (
                  <div className="pb-6 border-b border-border">
                    <ProfilePhotoUploader
                      currentImage={profile.profile_image}
                      onChange={(img) => setProfile({ ...profile, profile_image: img })}
                      dataTestId="profile-photo-section"
                    />
                  </div>
                )}
                {!profile && (
                  <div className="pb-6 border-b border-border">
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                      Save your profile first, then come back to upload a profile photo. A photo is required before you can be approved.
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Bio</label>
                  <textarea
                    value={profileData.bio}
                    onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                    className="input-field w-full min-h-[100px]"
                    placeholder="Tell customers about yourself..."
                    data-testid="bio-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Years of Experience</label>
                    <input
                      type="number"
                      min="0"
                      value={profileData.years_experience}
                      onChange={(e) => setProfileData({...profileData, years_experience: parseInt(e.target.value)})}
                      className="input-field w-full"
                      data-testid="experience-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {platformSettings?.pricing_mode === 'floor' ? 'Hourly Rate ($)' : 'Hourly Rate ($, fallback)'}
                    </label>
                    <input
                      type="number"
                      min="10"
                      value={profileData.hourly_rate}
                      onChange={(e) => setProfileData({...profileData, hourly_rate: parseFloat(e.target.value)})}
                      className="input-field w-full"
                      data-testid="rate-input"
                    />
                    {platformSettings?.pricing_mode !== 'floor' && (
                      <p className="text-xs text-text-secondary mt-1.5">
                        Rates are set by zip code on this platform. This number is only used as a fallback if your zip doesn&apos;t have a published rate yet.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Primary Zip Code</label>
                  <input
                    type="text"
                    value={profileData.zip_code}
                    onChange={(e) => setProfileData({...profileData, zip_code: e.target.value})}
                    className="input-field w-full"
                    placeholder="12345"
                    data-testid="zipcode-input"
                  />
                </div>

                {/* Service area: extra zips + travel radius */}
                <div data-testid="service-area-field">
                  <label className="block text-sm font-medium text-text-secondary mb-2">Additional service areas (optional)</label>
                  <p className="text-xs text-text-secondary mb-3">
                    Add other zip codes you&apos;re willing to travel to. Customers in nearby zips will also see you automatically (based on your travel radius below).
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="service-zips-list">
                    {(profileData.service_zip_codes || []).map((z) => (
                      <span
                        key={z}
                        className="inline-flex items-center gap-1 bg-primary-light text-primary text-xs font-semibold px-2 py-1 rounded-full"
                        data-testid={`service-zip-${z}`}
                      >
                        {z}
                        <button
                          type="button"
                          onClick={() => setProfileData({
                            ...profileData,
                            service_zip_codes: profileData.service_zip_codes.filter((x) => x !== z),
                          })}
                          className="ml-0.5 hover:text-red-600"
                          aria-label={`Remove ${z}`}
                          data-testid={`remove-service-zip-${z}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="Add a zip code (e.g. 08527)"
                      className="input-field flex-1"
                      data-testid="add-service-zip-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = e.target.value.trim();
                          if (v && !/[^0-9-]/.test(v) && !(profileData.service_zip_codes || []).includes(v)) {
                            setProfileData({
                              ...profileData,
                              service_zip_codes: [...(profileData.service_zip_codes || []), v],
                            });
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.currentTarget.previousSibling;
                        const v = input.value.trim();
                        if (v && !/[^0-9-]/.test(v) && !(profileData.service_zip_codes || []).includes(v)) {
                          setProfileData({
                            ...profileData,
                            service_zip_codes: [...(profileData.service_zip_codes || []), v],
                          });
                          input.value = '';
                        }
                      }}
                      className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark"
                      data-testid="add-service-zip-button"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Auto-include customers within{' '}
                      <span className="font-bold text-primary">{profileData.service_radius_miles} mi</span>{' '}
                      of your primary zip
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={50}
                      step={1}
                      value={profileData.service_radius_miles}
                      onChange={(e) => setProfileData({...profileData, service_radius_miles: parseInt(e.target.value, 10)})}
                      className="w-full"
                      data-testid="service-radius-slider"
                    />
                    <div className="flex justify-between text-[10px] text-text-secondary">
                      <span>1 mi</span><span>25 mi</span><span>50 mi</span>
                    </div>
                  </div>
                </div>

                <div data-testid="supplies-default-field">
                  <label className="block text-sm font-medium text-text-secondary mb-2">Who brings cleaning supplies?</label>
                  <p className="text-xs text-text-secondary mb-3">This is your default. Customers can request an override at booking time.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setProfileData({...profileData, supplies_default: 'cleaner'})}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${profileData.supplies_default === 'cleaner' ? 'border-primary bg-primary-light/30' : 'border-border hover:border-primary/40'}`}
                      data-testid="supplies-default-cleaner"
                    >
                      <div className="font-semibold text-text-primary mb-1">I bring my own supplies</div>
                      <div className="text-xs text-text-secondary">Cost is included in your hourly rate.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setProfileData({...profileData, supplies_default: 'customer'})}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${profileData.supplies_default === 'customer' ? 'border-primary bg-primary-light/30' : 'border-border hover:border-primary/40'}`}
                      data-testid="supplies-default-customer"
                    >
                      <div className="font-semibold text-text-primary mb-1">Customer provides supplies</div>
                      <div className="text-xs text-text-secondary">If they ask you to bring, you get the supplies surcharge on top of your rate.</div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Certifications</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newCert}
                      onChange={(e) => setNewCert(e.target.value)}
                      className="input-field flex-1"
                      placeholder="Add certification"
                    />
                    <button onClick={addCertification} className="btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profileData.certifications.map((cert, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary-light text-primary rounded-full text-sm">
                        {cert}
                        <button
                          onClick={() => setProfileData({
                            ...profileData,
                            certifications: profileData.certifications.filter((_, i) => i !== idx)
                          })}
                          className="ml-2 text-primary hover:text-primary-hover"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Services Offered</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newService}
                      onChange={(e) => setNewService(e.target.value)}
                      className="input-field flex-1"
                      placeholder="Add service"
                    />
                    <button onClick={addService} className="btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profileData.services.map((service, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary-light text-primary rounded-full text-sm">
                        {service}
                        <button
                          onClick={() => setProfileData({
                            ...profileData,
                            services: profileData.services.filter((_, i) => i !== idx)
                          })}
                          className="ml-2 text-primary hover:text-primary-hover"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  {/* TEMPORARILY HIDDEN — re-enable as a CALENDAR-based picker (not weekly day/time grid) once we go live.
                      Bring back: <Calendar mode="multiple" /> from /app/frontend/src/components/ui/calendar, store as ISO date list. */}
                  {false && (
                    <>
                      <label className="block text-sm font-medium text-text-secondary mb-3">Availability</label>
                      <div className="space-y-4">
                        {daysOfWeek.map((day) => (
                          <div key={day}>
                            <p className="text-sm font-medium text-text-primary mb-2 capitalize">{day}</p>
                            <div className="flex flex-wrap gap-2">
                              {timeSlots.map((time) => (
                                <button
                                  key={time}
                                  onClick={() => toggleAvailability(day, time)}
                                  className={`px-3 py-1 rounded-lg text-sm transition-all duration-300 ${
                                    profileData.availability[day]?.includes(time)
                                      ? 'bg-primary text-white'
                                      : 'bg-surface-muted text-text-secondary hover:bg-primary-light'
                                  }`}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-4">
                  <button onClick={handleSaveProfile} className="btn-primary" data-testid="save-profile-button">
                    Save Profile
                  </button>
                  {profile && (
                    <button onClick={() => setShowProfileForm(false)} className="btn-secondary">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stripe Connect onboarding — only after admin approval */}
          {profile && !showProfileForm && profile.approval_status === 'approved' && (
            <div className="mb-6">
              <StripeConnectCard />
            </div>
          )}

          {/* Refer-a-friend panel */}
          {profile && !showProfileForm && (
            <ReferralPanel />
          )}

          {/* Bookings */}
          {profile && !showProfileForm && (
            <div className="bg-surface rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h2 className="font-heading text-2xl font-semibold text-text-primary">{t('cleaner.upcomingBookings', 'Upcoming Bookings')}</h2>
                {bookings.length > 0 && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <CalendarSubscribe />
                    <div className="inline-flex bg-surface-muted rounded-full p-1" role="group" data-testid="cleaner-bookings-view-toggle">
                    <button
                      onClick={() => setBookingsView('list')}
                      className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${bookingsView === 'list' ? 'bg-text-primary text-white' : 'text-text-secondary hover:text-text-primary'}`}
                      data-testid="cleaner-view-list-button"
                      aria-pressed={bookingsView === 'list'}
                    >
                      <List className="w-3.5 h-3.5" />
                      {t('common.viewList', 'List')}
                    </button>
                    <button
                      onClick={() => setBookingsView('calendar')}
                      className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${bookingsView === 'calendar' ? 'bg-text-primary text-white' : 'text-text-secondary hover:text-text-primary'}`}
                      data-testid="cleaner-view-calendar-button"
                      aria-pressed={bookingsView === 'calendar'}
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                      {t('common.viewCalendar', 'Calendar')}
                    </button>
                    </div>
                  </div>
                )}
              </div>
              {bookings.length === 0 ? (
                <p className="text-text-secondary">{t('cleaner.noActiveBookings', 'No bookings yet.')}</p>
              ) : bookingsView === 'calendar' ? (
                <BookingsCalendar bookings={bookings} perspective="cleaner" onSelectBooking={() => setBookingsView('list')} />
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="p-4 bg-surface-muted rounded-xl" data-testid="cleaner-booking-card">
                      {/* Travel from previous job */}
                      {booking.travel_minutes_from_previous != null && (
                        <div
                          className={`mb-2 inline-flex items-center gap-2 text-[11px] font-medium px-2.5 py-1 rounded-full ${
                            booking.travel_warning
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : 'bg-blue-50 text-blue-800 border border-blue-200'
                          }`}
                          data-testid={`travel-chip-${booking.id}`}
                        >
                          {booking.travel_warning ? <AlertTriangle className="w-3 h-3" /> : <Car className="w-3 h-3" />}
                          <span>
                            {booking.travel_minutes_from_previous} min drive
                            {booking.travel_miles_from_previous != null && ` · ${booking.travel_miles_from_previous} mi`}
                            {' from previous job'}
                            {booking.travel_warning && booking.buffer_minutes != null && (
                              <span className="ml-1 font-semibold">
                                — only {booking.buffer_minutes} min gap!
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-text-primary mb-1">{booking.customer_name}</h3>
                          <div className="flex items-center gap-4 text-sm text-text-secondary flex-wrap">
                            <span>{booking.service_date} {t('booking.at', 'at')} {booking.service_time}</span>
                            <span>{booking.service_type}</span>
                            {booking.flexible && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full" data-testid={`flexible-badge-${booking.id}`}>
                                Flexible · {booking.flexible_window_start}–{booking.flexible_window_end} · {booking.duration_hours}h
                              </span>
                            )}
                            {booking.place_label && (
                              <span className="inline-flex items-center gap-1 text-xs bg-surface px-2 py-0.5 rounded-full">
                                <MapPin className="w-3 h-3" />
                                {booking.place_label} ({booking.property_type})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-heading text-xl font-bold text-primary">${booking.total_amount.toFixed(2)}</p>
                          <span className="text-xs text-text-secondary capitalize">{t(`dashboard.status.${booking.status === 'pending_payment' ? 'pendingPayment' : booking.status === 'in_progress' ? 'inProgress' : booking.status}`, booking.status.replace(/_/g, ' '))}</span>
                          {booking.status !== 'cancelled' && booking.status !== 'declined_by_cleaner' && (
                            <div className="mt-2">
                              <AddToCalendarButton bookingId={booking.id} />
                            </div>
                          )}
                        </div>
                      </div>

                      {booking.status === 'awaiting_cleaner_response' && (
                        <CleanerBookingResponseActions
                          booking={booking}
                          onResolved={fetchData}
                        />
                      )}

                      {booking.status === 'change_proposed' && booking.proposed_changes && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800" data-testid="awaiting-customer-response">
                          <p className="font-semibold mb-1">Awaiting customer response</p>
                          <p>You proposed: {booking.proposed_changes.new_service_date} {booking.proposed_changes.new_start_time}–{booking.proposed_changes.new_end_time}</p>
                          {booking.proposed_changes.note && <p className="italic mt-1">&ldquo;{booking.proposed_changes.note}&rdquo;</p>}
                        </div>
                      )}

                      {booking.status === 'declined_by_cleaner' && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                          You declined this booking. Refund is being processed.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleanerDashboard;
